import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TextInput } from '../shared/components/inputs/TextInput';
import { AuthPageLayout } from '../shared/components/containers/AuthPageLayout';
import { Form } from 'react-bootstrap';
import { PasswordInput } from '../shared/components/inputs/PasswordInput';
import { PrimaryButton } from '../shared/components/buttons/PrimaryButton';
import { BasicAnchor } from '../shared/components/links/BasicAnchor';
import { BrandHeader } from '../shared/components/headers/BrandHeader';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../api/client';
import { useNavigate } from 'react-router-dom';

interface OwnProps {
}

const StyledTextInput = styled(TextInput)`
  width: 100%;
  max-width: 350px;
  margin-bottom: 20px;
`;

const StyledPasswordInput = styled(PasswordInput)`
  width: 100%;
  max-width: 350px;
  margin-bottom: 20px;
`;

const InFormHeader = styled.div`
  font-weight: 600;
  font-size: 1.3em;
  margin-bottom: 5px;
`;

const HeaderExplanation = styled(Form.Text)`
  margin-bottom: 15px;
  display: flex;
`;

const ButtonRow = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginRow = styled.div`
  margin-top: 30px;
`;

const LoginAnchor = styled(BasicAnchor)`
  margin-left: 1ch;
`;

const ErrorText = styled(Form.Text)`
  color: ${(props) => props.theme.colors.error};
  display: block;
  margin-bottom: 15px;
`;

const RegisterPage = (props: OwnProps) => {
  const {} = props;
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onRegisterClick = async () => {
    if (password !== passwordConfirm) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await register(email, password, passwordConfirm);
      navigate('/');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('register.errors.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <BrandHeader/>

      <InFormHeader>{t('register.heading')}</InFormHeader>
      <HeaderExplanation muted>{t('register.explanation')}</HeaderExplanation>

      <Form.Label htmlFor="register-email">{t('register.emailLabel')}</Form.Label>
      <StyledTextInput id="register-email" onChange={setEmail} value={email} type='email'/>

      <Form.Label htmlFor="register-password">{t('register.passwordLabel')}</Form.Label>
      <StyledPasswordInput id="register-password" onChange={setPassword} value={password}/>

      <Form.Label htmlFor="register-password-confirm">{t('register.passwordConfirmLabel')}</Form.Label>
      <StyledPasswordInput id="register-password-confirm" onChange={setPasswordConfirm} value={passwordConfirm}/>

      {error && <ErrorText>{error}</ErrorText>}

      <ButtonRow>
        <PrimaryButton onClick={onRegisterClick} disabled={submitting}>
          {submitting ? t('register.submitting') : t('register.submit')}
        </PrimaryButton>
      </ButtonRow>

      <LoginRow>
        <Form.Text muted>
          {t('register.hasAccount')}
          <LoginAnchor url='/'>{t('register.logInHere')}</LoginAnchor>
        </Form.Text>
      </LoginRow>
    </AuthPageLayout>
  );
};

export {
  RegisterPage,
};
